/**
 * UI hints are optional metadata that consumers (like the configurator generator
 * in scripts/post-build.ts) read to render forms. The runtime parser ignores them.
 */
interface UiHints {
  /** Human-readable label for the field. */
  label?: string;
  /** Group/section title for visual grouping. */
  group?: string;
  /** Numeric step (only meaningful for `type: "number"`). */
  step?: number;
  /** Minimum value (only meaningful for `type: "number"`). */
  min?: number;
  /** Maximum value (only meaningful for `type: "number"`). */
  max?: number;
}

export type AttrSchemaValue =
  | ({ type: "string"; default: string } & UiHints)
  | ({ type: "number"; default: number } & UiHints)
  | ({ type: "boolean"; default: boolean } & UiHints)
  | ({ type: "enum"; values: readonly string[]; default: string } & UiHints);

export type AttrSchema = Record<string, AttrSchemaValue>;

export type PropsOf<S extends AttrSchema> = {
  [K in keyof S]: S[K] extends { type: "number" }
    ? number
    : S[K] extends { type: "boolean" }
      ? boolean
      : S[K] extends { type: "enum"; values: readonly (infer V extends string)[] }
        ? V
        : string;
};

const camelToKebab = (s: string): string => s.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);

const parseValue = (raw: string | null, def: AttrSchemaValue): unknown => {
  if (raw === null) return def.default;
  switch (def.type) {
    case "string":
      return raw;
    case "number": {
      const n = Number(raw);
      return Number.isFinite(n) ? n : def.default;
    }
    case "boolean":
      return raw === "" || raw === "true" || raw === "1";
    case "enum":
      return def.values.includes(raw) ? raw : def.default;
  }
};

export const parseAttrs = <S extends AttrSchema>(host: Element, schema: S): PropsOf<S> => {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(schema)) {
    const def = schema[key];
    if (def === undefined) continue;
    const attrName = `data-${camelToKebab(key)}`;
    result[key] = parseValue(host.getAttribute(attrName), def);
  }
  return result as PropsOf<S>;
};
